import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import AddProductToSale from "./AddProductToSale";
import { GetPromotions, updatePromotion } from "../../../server/PriceList";
import { getProductByProductsIds } from "../../../server/Product";
import { setPreload } from "../../../server/CartsOpen";
import showAlert from "../../../server/ShowAlert";
import ProductPageWithImg from "../productPage/ProductPageWithImg";
import { Plus, Trash2, PackageSearch } from "lucide-react";
import {
  getGtinsFromPromotionEntries,
  getManufacturerPackQuantity,
  getProductCurrentPrice,
  getPromoId,
  getPromotionApiErrorMessage,
  isPackagePromo,
  validatePromotionBeforeSave,
} from "./promotionUtils";

const getProductGtin = (item) => {
  if (typeof item === "string") return item;
  return item?.gtin || item?.ProductMkt || null;
};

const normalizeProductList = (gtins) =>
  (gtins || [])
    .map((item) => {
      const gtin = getProductGtin(item);
      if (!gtin) return null;
      return { gtin };
    })
    .filter(Boolean);

const getTargetProductsFromSale = (sale) => {
  if (!sale) return [];
  if (Array.isArray(sale.targetProducts) && sale.targetProducts.length > 0) {
    return sale.targetProducts
      .map((tp) => (typeof tp === "string" ? tp : tp?.gtin))
      .filter(Boolean);
  }
  return getGtinsFromPromotionEntries([sale]);
};

const fetchProductsByGtins = async (gtins, branch) => {
  if (!gtins?.length || !branch) return [];
  const productIds = gtins.map((g) => `${g}-000`).join(",");
  const response = await getProductByProductsIds(productIds, branch);
  return Array.isArray(response.data) ? response.data : [];
};

const ProductInSale = ({ sale, checkedDates }) => {
  const dispatch = useDispatch();
  const stationId = useSelector((state) => state.user.stationId);
  const storeProducts = useSelector(
    (state) => state.displayProduct.itemsShow.products,
  );
  const [showAddForm, setShowAddForm] = useState(false);
  const [productList, setProductList] = useState([]);
  const [saleProducts, setSaleProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productPopUp, setProductPopUp] = useState(false);
  const saleId = getPromoId(sale);

  const syncProductList = useCallback(async () => {
    const fallbackGtins = getTargetProductsFromSale(sale);

    if (!saleId || !stationId) {
      setProductList(normalizeProductList(fallbackGtins));
      return;
    }

    try {
      const promos = await GetPromotions({ branch: stationId });
      const relatedPromos = (promos || []).filter(
        (p) => String(getPromoId(p)) === String(saleId),
      );
      const gtins =
        relatedPromos.length > 0
          ? getGtinsFromPromotionEntries(relatedPromos)
          : fallbackGtins;
      setProductList(normalizeProductList(gtins));
    } catch {
      setProductList(normalizeProductList(fallbackGtins));
    }
  }, [sale, saleId, stationId]);

  useEffect(() => {
    syncProductList();
  }, [syncProductList]);

  useEffect(() => {
    const gtins = productList.map(getProductGtin).filter(Boolean);
    if (!gtins.length || !stationId) {
      setSaleProducts([]);
      return;
    }

    let cancelled = false;
    setProductsLoaded(false);

    fetchProductsByGtins(gtins, stationId)
      .then((items) => {
        if (!cancelled) setSaleProducts(items);
      })
      .catch(() => {
        if (!cancelled) setSaleProducts([]);
      })
      .finally(() => {
        if (!cancelled) setProductsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [productList, stationId]);

  const findProduct = (gtin) => {
    const gtinStr = String(gtin);
    return (
      saleProducts.find((p) => String(p.gtin) === gtinStr) ||
      storeProducts?.find((p) => String(p.gtin) === gtinStr) ||
      null
    );
  };

  const updateProduct = async (gtin, add) => {
    if (!gtin) {
      showAlert({ msgTitle: "יש להזין ברקוד מוצר", msgType: "error" });
      return;
    }
    if (!saleId) {
      showAlert({ msgTitle: "מזהה מבצע חסר", msgType: "error" });
      return;
    }

    const from = sale?.startDate || sale?.FromDate;
    const to = sale?.endDate || sale?.ToDate;
    if (!checkedDates(from, to)) {
      showAlert({
        msgTitle: "המבצע כבר התחיל, לא ניתן לשנות הגדרות",
        msgType: "warning",
      });
      return;
    }

    const currentGtins = productList.map(getProductGtin);

    if (add && currentGtins.includes(String(gtin))) {
      showAlert({ msgTitle: "המוצר כבר נמצא במבצע", msgType: "error" });
      return;
    }

    // One promotion carries a single minQuantity, so every product in a package
    // promotion must come in the same manufacturer pack.
    if (add && isPackagePromo(sale)) {
      const candidate = await fetchProductsByGtins([gtin], stationId)
        .then((items) => items[0] || null)
        .catch(() => null);

      if (!candidate) {
        showAlert({ msgTitle: "לא נמצאו פרטי מוצר", msgType: "error" });
        return;
      }

      const packQuantity = getManufacturerPackQuantity(candidate);
      const promoPackQuantity = Number(sale.minQuantity) || 0;

      if (packQuantity !== promoPackQuantity) {
        showAlert({
          msgTitle: "כמות במארז יצרן אינה תואמת למבצע",
          msgText: `למוצר מוגדרת כמות ${packQuantity || "לא מוגדר"} ואילו המבצע הוא על מארז של ${promoPackQuantity}`,
          msgType: "error",
        });
        return;
      }
    }

    const updatedTargetProducts = add
      ? [...currentGtins, String(gtin)]
      : currentGtins.filter((g) => String(g) !== String(gtin));

    if (add) {
      let productToAdd = findProduct(gtin);
      if (!productToAdd) {
        try {
          const fetched = await fetchProductsByGtins([String(gtin)], stationId);
          productToAdd = fetched[0] || null;
        } catch {
          productToAdd = null;
        }
      }

      const productPriceByGtin = {
        [String(gtin)]: getProductCurrentPrice(productToAdd),
      };
      const validationError = validatePromotionBeforeSave({
        branch: stationId,
        promoType: sale?.promoType,
        status: sale?.status,
        discountValue: sale?.discountValue,
        minQuantity: sale?.minQuantity,
        startDate: sale?.startDate,
        endDate: sale?.endDate,
        targetGtins: [String(gtin)],
        productPriceByGtin,
        existingPromotions: [],
        currentPromoId: saleId,
      });
      if (validationError) {
        showAlert({
          msgTitle: "לא ניתן להוסיף את המוצר למבצע",
          msgText: validationError,
          msgType: "error",
        });
        return;
      }
    }

    dispatch(setPreload(true));
    try {
      const result = await updatePromotion(saleId, {
        targetProducts: updatedTargetProducts,
      });
      const resolvedGtins = getTargetProductsFromSale(result);
      const finalGtins =
        resolvedGtins.length > 0 ? resolvedGtins : updatedTargetProducts;

      setProductList(normalizeProductList(finalGtins));
      setShowAddForm(false);
      showAlert({
        msgTitle: add ? "המוצר התווסף בהצלחה" : "המוצר הוסר מהמבצע בהצלחה",
        msgType: "success",
      });
    } catch (error) {
      const message = getPromotionApiErrorMessage(error, "שגיאה בעדכון המבצע");
      showAlert({
        msgTitle: "לא ניתן לעדכן את המבצע",
        msgText: message,
        msgType: "error",
      });
    } finally {
      dispatch(setPreload(false));
    }
  };

  const openProductPopup = (gtin) => {
    const product = findProduct(gtin);
    if (!product) {
      showAlert({ msgTitle: "לא נמצאו פרטי מוצר", msgType: "error" });
      return;
    }
    setSelectedProduct(product);
    setProductPopUp(true);
  };

  return (
    <div className="sale-panel">
      {productPopUp && selectedProduct && (
        <ProductPageWithImg
          selectedProd={selectedProduct}
          close={() => {
            setProductPopUp(false);
            setSelectedProduct(null);
          }}
        />
      )}

      <div className="sale-panel__header">
        <h2 className="sale-panel__title">
          <PackageSearch aria-hidden size={17} strokeWidth={2} />
          מוצרים במבצע
        </h2>
        {!showAddForm && (
          <button
            type="button"
            className="sale-panel__toggle-btn"
            onClick={() => setShowAddForm(true)}
          >
            <Plus aria-hidden size={15} strokeWidth={2.4} />
            הוספת מוצר
          </button>
        )}
      </div>

      {showAddForm ? (
        <AddProductToSale
          updateProduct={updateProduct}
          onCancel={() => setShowAddForm(false)}
        />
      ) : null}

      {productList?.length > 0 ? (
        <div className="sale-panel__table-scroll">
          <table className="sale-panel__table">
            <thead>
              <tr>
                <th>שם מוצר</th>
                <th>ברקוד</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {productList.map((x, index) => {
                const gtin = getProductGtin(x);
                const product = findProduct(gtin);
                const name = product?.name;

                return (
                  <tr key={gtin || index}>
                    <td>
                      {!productsLoaded ? (
                        <span className="sale-panel__loading-name">
                          טוען...
                        </span>
                      ) : product ? (
                        <button
                          type="button"
                          className="sale-detail-card__product-link"
                          onClick={() => openProductPopup(gtin)}
                        >
                          {name}
                        </button>
                      ) : (
                        <span className="sale-panel__loading-name">
                          מוצר לא נמצא
                        </span>
                      )}
                    </td>
                    <td>{gtin}</td>
                    <td>
                      <div className="sale-panel__actions">
                        <button
                          type="button"
                          className="sale-panel__icon-btn sale-panel__icon-btn--danger"
                          onClick={() => updateProduct(gtin, false)}
                          title="הסרת מוצר"
                          aria-label="הסרת מוצר"
                        >
                          <Trash2 aria-hidden size={14} strokeWidth={2.2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="sale-panel__empty">אין מוצרים משויכים למבצע</div>
      )}
    </div>
  );
};

export default ProductInSale;
